import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion as ReactAccordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel
} from 'react-accessible-accordion';
import '../../../../../javascripts/shims/findIndex';
import SvgSprite from '../../../../atoms/svg-sprite/SvgSprite.jsx';
import Image from '../../../../atoms/image/Image.jsx';

const ContactLayer = ({ subEntry }) => (
  <div className="o-header__contact-block">
    {subEntry.image && (
      <div className="o-header__contact-image">
        <Image {...subEntry.image} />
      </div>
    )}
    <div className="o-header__contact-text">
      {subEntry.name && <span className="a-copy">{subEntry.name}</span>}
      {subEntry.title && <span className="a-copy a-copy--small">{subEntry.title}</span>}
      {subEntry.address && (
        <span
          className="a-copy"
          /* eslint-disable-next-line react/no-danger */
          dangerouslySetInnerHTML={{ __html: subEntry.address }}
        />
      )}
      {subEntry.entries.map(entry => (
        <div className="o-header__sub-entry o-header__icon-link">
          {!entry.href.includes(window.location.hostname) ? (
            <a
              href={entry.href}
              target="_blank"
              rel="noopener noreferrer"
              title={entry.label}
              className="a-link a-link--hover-petrol"
            >
              <SvgSprite
                id="default-arrow-small"
                title="default-arrow-small"
                className="a-link__icon"
              />
              {entry.type === 'contactInfo' ? entry.href.replace(/^[^:]*:/, '') : entry.label}
            </a>
          ) : (
            <a href={entry.href} title={entry.label} className="a-link a-link--hover-petrol">
              <SvgSprite
                id="default-arrow-small"
                title="default-arrow-small"
                className="a-link__icon"
              />
              {entry.type === 'contactInfo' ? entry.href.replace(/^[^:]*:/, '') : entry.label}
            </a>
          )}
        </div>
      ))}
    </div>
  </div>
);

const SubEntries = ({ subEntry }) => {
  const redirectForm = useRef();

  const handleClick = (e, href) => {
    e.preventDefault();
    const slug = href.replace(/^(?:\/\/|[^/]+)*\//, '');
    redirectForm.current.setAttribute('action', `//${window.location.hostname}/prg/${slug}`);
    redirectForm.current.submit();
  };

  if (subEntry.hasPrgMasking) {
    return (
      <form ref={redirectForm} action="" method="post">
        <ul>
          {subEntry.entries.map(entry => (
            <li className="o-header__sub-entry">
              <button
                type="submit"
                onClick={e => handleClick(e, entry.href)}
                className="a-link a-link--hover-opacity"
              >
                {entry.label}
              </button>
            </li>
          ))}
          {subEntry.link && (
            <li className="o-header__sub-entry o-header__icon-link">
              <a
                href={subEntry.link.href}
                title={subEntry.link.label}
                className="a-link a-link--hover-petrol"
              >
                <SvgSprite
                  id="default-arrow-small"
                  title="default-arrow-small"
                  className="a-link__icon"
                />
                {subEntry.link.label}
              </a>
            </li>
          )}
        </ul>
      </form>
    );
  }

  return (
    <>
      <ul>
        {subEntry.entries.map(entry => (
          <li className="o-header__sub-entry">
            <a href={entry.href} title={entry.label} className="a-link a-link--hover-opacity">
              {entry.label}
            </a>
          </li>
        ))}
        {subEntry.link && (
          <li className="o-header__sub-entry o-header__icon-link">
            <a
              href={subEntry.link.href}
              title={subEntry.link.label}
              className="a-link a-link--hover-petrol"
            >
              <SvgSprite
                id="default-arrow-small"
                title="default-arrow-small"
                className="a-link__icon"
              />
              {subEntry.link.label}
            </a>
          </li>
        )}
      </ul>
    </>
  );
};

const MobileSubNav = ({ mainEntry }) => (
  <ReactAccordion allowZeroExpanded className="o-header__sub-navigation">
    <ul className="o-header__sub-list">
      {mainEntry.children.map(subEntry => (
        <li className="o-header__sub-block">
          <AccordionItem key={subEntry.label}>
            <AccordionItemHeading>
              <AccordionItemButton>
                <div className="o-header__label-wrapper">
                  <span className="o-header__sub-headline">{subEntry.headline}</span>
                  <SvgSprite
                    id="default-arrow-down"
                    title="default-arrow-down"
                    className="accordion__icon"
                  />
                </div>
              </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel>
              {mainEntry.type !== 'contact' ? (
                <SubEntries subEntry={subEntry} />
              ) : (
                <ContactLayer subEntry={subEntry} />
              )}
            </AccordionItemPanel>
          </AccordionItem>
        </li>
      ))}
    </ul>
    {mainEntry.link && (
      <div className="o-header__label-wrapper">
        <a
          href={mainEntry.link.href}
          title={mainEntry.link.label}
          className="a-link a-link--petrol a-link--hover-opacity"
        >
          {mainEntry.link.label}
        </a>
      </div>
    )}
  </ReactAccordion>
);

const DesktopSubNav = ({ mainEntry }) => (
  <div className="o-header__sub-navigation">
    <ul className="o-header__sub-list">
      {mainEntry.children.map(subEntry => (
        <li
          className={`o-header__sub-block${
            subEntry.hasHighlight ? ' o-header__sub-block--has-highlight' : ''
          }${subEntry.image ? ' o-header__sub-block--has-image' : ''}`}
        >
          <span className="o-header__sub-headline">{subEntry.headline}</span>
          {mainEntry.type !== 'contact' ? (
            <SubEntries subEntry={subEntry} />
          ) : (
            <ContactLayer subEntry={subEntry} />
          )}
        </li>
      ))}
    </ul>

    <div className="o-header__label-wrapper o-header__main-overview">
      {mainEntry.link && (
        <a
          href={mainEntry.link.href}
          title={mainEntry.link.label}
          className="a-link a-link--petrol a-link--hover-opacity"
        >
          {mainEntry.link.label}
        </a>
      )}
    </div>
  </div>
);

const Navigation = ({ navigation }) => {
  const [isMobile, setMobile] = useState(window.innerWidth < 1024);
  const [expandedMobileNav, expandMobileNav] = useState(false);
  const [currentEntry, setCurrentEntry] = useState('');
  const navRef = useRef(null);

  const handleResponsiveBehavior = () => {
    if (window.innerWidth < 1024) {
      setMobile(true);
    } else {
      setMobile(false);
    }
  };

  const handleOutSideClick = e => {
    if (navRef.current && !navRef.current.contains(e.target)) {
      setCurrentEntry('');
    }
  };

  useEffect(() => {
    handleResponsiveBehavior();
    window.addEventListener('resize', handleResponsiveBehavior);
    document.addEventListener('click', handleOutSideClick);
    return () => {
      window.removeEventListener('resize', handleResponsiveBehavior);
      document.removeEventListener('click', handleOutSideClick);
    };
  }, []);

  return (
    <nav className="o-header__navigation" ref={navRef}>
      {isMobile && (
        <button
          className="o-header__burger"
          type="button"
          onClick={() => expandMobileNav(!expandedMobileNav)}
        >
          {expandedMobileNav === false ? (
            <SvgSprite id="default-burger" title="burger" />
          ) : (
            <SvgSprite id="default-close" title="close" />
          )}
        </button>
      )}

      {isMobile ? (
        expandedMobileNav && (
          <ReactAccordion allowZeroExpanded className="o-header__main-navigation">
            <ul className="o-header__main-list">
              {navigation.map(mainEntry =>
                !mainEntry.flyout && mainEntry.type !== 'contact' && mainEntry.link ? (
                  <li className="o-header__main-entry">
                    <a
                      href={mainEntry.link.href}
                      title={mainEntry.label}
                      className={`o-header__main-link${
                        mainEntry.active && currentEntry === '' ? ' active' : ''
                      }`}
                    >
                      <div className="o-header__label-wrapper">
                        <span className="o-header__main-label">{mainEntry.label}</span>
                      </div>
                    </a>
                  </li>
                ) : (
                  <li className="o-header__main-entry">
                    <AccordionItem key={mainEntry.label}>
                      <AccordionItemHeading>
                        <AccordionItemButton>
                          <div className="o-header__label-wrapper">
                            <span className="o-header__main-label">{mainEntry.label}</span>
                          </div>
                        </AccordionItemButton>
                      </AccordionItemHeading>
                      <AccordionItemPanel>
                        <MobileSubNav mainEntry={mainEntry} />
                      </AccordionItemPanel>
                    </AccordionItem>
                  </li>
                )
              )}
            </ul>
          </ReactAccordion>
        )
      ) : (
        <div className="o-header__main-navigation">
          <ul className="o-header__main-list">
            {navigation.map((mainEntry, index) => (
              <li
                className="o-header__main-entry"
                key={mainEntry.label}
                onMouseEnter={() => setCurrentEntry(mainEntry.label)}
                onMouseLeave={() => setCurrentEntry('')}
              >
                {mainEntry.link && !mainEntry.flyout ? (
                  <a
                    id={`o-header__main-link-${index}`}
                    href={mainEntry.link.href}
                    onFocus={() => setCurrentEntry(mainEntry.label)}
                    className={`o-header__main-link${
                      mainEntry.type === 'contact' ? ' contact' : ''
                    }${mainEntry.active && currentEntry === '' ? ' active' : ''}`}
                  >
                    <div className="o-header__label-wrapper">
                      <span className="o-header__main-label">{mainEntry.label}</span>
                    </div>
                  </a>
                ) : (
                  <div
                    id={`o-header__main-link-${index}`}
                    role="button"
                    aria-haspopup={mainEntry.flyout}
                    aria-expanded={currentEntry === mainEntry.label}
                    aria-controls={mainEntry.flyout && `o-header__panel-${index}`}
                    onFocus={() => setCurrentEntry(mainEntry.label)}
                    className={`o-header__main-link${
                      mainEntry.type === 'contact' ? ' contact' : ''
                    }${mainEntry.active && currentEntry === '' ? ' active' : ''}`}
                  >
                    <div className="o-header__label-wrapper">
                      <span className="o-header__main-label">{mainEntry.label}</span>
                    </div>
                  </div>
                )}
                {mainEntry.flyout && currentEntry === mainEntry.label && (
                  <div
                    id={`o-header__panel-${index}`}
                    className="o-header__panel"
                    aria-labelledby={`o-header__main-entry-${index}`}
                  >
                    <div className="l-container l-container--has-margin">
                      <div className="l-container__wrapper">
                        <div className="l-container__grid">
                          <DesktopSubNav mainEntry={mainEntry} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </nav>
  );
};

SubEntries.propTypes = {
  subEntry: PropTypes.shape().isRequired
};

ContactLayer.propTypes = {
  subEntry: PropTypes.shape().isRequired
};

MobileSubNav.propTypes = {
  mainEntry: PropTypes.shape().isRequired
};

DesktopSubNav.propTypes = {
  mainEntry: PropTypes.shape().isRequired
};

Navigation.propTypes = {
  navigation: PropTypes.arrayOf(PropTypes.shape()).isRequired
};

export default Navigation;
